/**
 * Origin Detection Utility for Whitelabel Multi-Tenant Architecture
 *
 * This module detects iframe parent origin to determine tenant branding.
 *
 * Detection Strategy (BR-WL001):
 * Priority 1: window.parent.location.origin (same-origin, no CORS)
 * Priority 2: PostMessage communication (cross-origin)
 * Priority 3: Fallback to null (standalone mode → default branding, no tenant)
 *
 * Use Cases:
 * - Same-origin embedding: Parent and iframe on same domain (e.g., both *.croixbleue.ca)
 * - Cross-origin embedding: Parent and iframe on different domains (CORS blocks direct access)
 * - Standalone mode: App accessed directly (no iframe parent)
 *
 * Security Considerations:
 * - PostMessage origin validation (check event.origin against whitelist)
 * - Timeout after 5s to prevent indefinite waiting
 * - Graceful fallback to default branding if detection fails
 *
 * @module originDetection
 */

/**
 * Detects iframe parent origin URL.
 *
 * This function attempts to detect the origin of the parent window/app
 * using multiple strategies with fallback:
 *
 * Strategy 1: Same-Origin Detection (window.parent.location.origin)
 * - Works when parent and iframe are on same domain
 * - No CORS restrictions
 * - Example: Parent on https://app.croixbleue.ca, iframe on https://claims.croixbleue.ca
 * - Result: "https://app.croixbleue.ca"
 *
 * Strategy 2: Cross-Origin Detection (PostMessage)
 * - Works when parent and iframe are on different domains
 * - Parent receives REQUEST_ORIGIN message, responds with ORIGIN_RESPONSE
 * - Example: Parent on https://app.croixbleue.ca, iframe on https://claims.canassistance.com
 * - Result: event.origin from PostMessage response
 *
 * Strategy 3: Fallback to null (Standalone Mode)
 * - App accessed directly (no iframe parent)
 * - Timeout after 5s if no response
 * - Result: null → default branding, no tenant resolved
 *
 * Example Usage:
 * ```typescript
 * const origin = await detectOrigin();
 * if (origin) {
 *   // Resolve tenant ID from BFF
 *   const response = await axios.get(`/api/tenants/resolve-origin?origin=${origin}`);
 *   const tenantId = response.data.tenantId;
 * } else {
 *   // No origin to resolve: the default look, with no tenant claimed
 * }
 * ```
 *
 * @returns Promise<string | null> - Parent origin URL or null if detection fails/standalone
 */
export async function detectOrigin(): Promise<string | null> {
  try {
    // Attempt 1: Same-origin detection (no CORS)
    if (window.parent && window.parent !== window) {
      try {
        const parentOrigin = window.parent.location.origin;
        return parentOrigin;
      } catch (error) {
        // CORS blocked - parent is cross-origin
      }
    } else {
      // No parent window - standalone mode
      return null;
    }

    // Attempt 2: Cross-origin detection via PostMessage
    const originViaPostMessage = await detectOriginViaPostMessage();
    if (originViaPostMessage) {
      return originViaPostMessage;
    }

    // Fallback: Detection failed
    return null;

  } catch (error) {
    // Origin detection failed - gracefully fallback to standalone mode
    return null;
  }
}

/**
 * Detects origin via PostMessage communication (cross-origin).
 *
 * PostMessage Flow:
 * 1. Iframe sends REQUEST_ORIGIN message to parent: window.parent.postMessage({ type: 'REQUEST_ORIGIN' }, '*')
 * 2. Parent receives message, responds with ORIGIN_RESPONSE: iframe.postMessage({ type: 'ORIGIN_RESPONSE' }, iframeOrigin)
 * 3. Iframe extracts origin from event.origin property
 * 4. Timeout after 5s if no response
 *
 * Parent App Implementation (Required):
 * ```typescript
 * // Parent app must listen for REQUEST_ORIGIN and respond
 * window.addEventListener('message', (event) => {
 *   if (event.data?.type === 'REQUEST_ORIGIN') {
 *     // Respond with ORIGIN_RESPONSE
 *     event.source.postMessage({
 *       type: 'ORIGIN_RESPONSE',
 *       origin: window.location.origin
 *     }, event.origin);
 *   }
 * });
 * ```
 *
 * Security Notes:
 * - event.origin is tamper-proof (provided by browser)
 * - Always validate event.origin against whitelist in production
 * - Use '*' for postMessage target in development only
 *
 * @returns Promise<string | null> - Parent origin or null if timeout/error
 */
function detectOriginViaPostMessage(): Promise<string | null> {
  return new Promise((resolve) => {
    const TIMEOUT_MS = 5000; // 5 seconds

    // Set timeout to prevent indefinite waiting
    const timeout = setTimeout(() => {
      cleanup();
      resolve(null);
    }, TIMEOUT_MS);

    // Message handler for ORIGIN_RESPONSE
    const handler = (event: MessageEvent) => {
      // Check if this is the response we're waiting for
      if (event.data?.type === 'ORIGIN_RESPONSE') {
        cleanup();

        // Use event.origin (tamper-proof) instead of event.data.origin
        resolve(event.origin);
      }
    };

    // Cleanup function to remove listener and clear timeout
    const cleanup = () => {
      clearTimeout(timeout);
      window.removeEventListener('message', handler);
    };

    // Register message listener
    window.addEventListener('message', handler);

    // Send REQUEST_ORIGIN to parent
    // Using '*' as targetOrigin because we don't know parent origin yet
    // Parent will validate origin before responding
    try {
      window.parent.postMessage({ type: 'REQUEST_ORIGIN' }, '*');
    } catch (error) {
      cleanup();
      resolve(null);
    }
  });
}

/**
 * Validates if origin is whitelisted (security check).
 *
 * This function checks if detected origin is in expected whitelist.
 * In production, should validate against server-side whitelist.
 *
 * Known Origins (Example):
 * - https://app.croixbleue.ca (Croix Bleue production)
 * - https://app-staging.croixbleue.ca (Croix Bleue staging)
 * - http://localhost:5173 (Local development)
 *
 * Security Note:
 * This is client-side validation only (can be bypassed).
 * Server-side validation in TenantService is authoritative.
 *
 * @param origin - Origin URL to validate
 * @returns boolean - True if origin is recognized, false otherwise
 */
export function isOriginWhitelisted(origin: string | null): boolean {
  if (!origin) {
    return false;
  }

  // Known origins (should match backend TenantService.ORIGIN_TO_TENANT)
  const WHITELISTED_ORIGINS = [
    'https://app.croixbleue.ca',
    'https://app-staging.croixbleue.ca',
    'https://app-dev.croixbleue.ca',
    'http://localhost:5173', // Vite dev server
    'http://127.0.0.1:5173',
  ];

  const whitelisted = WHITELISTED_ORIGINS.includes(origin);

  return whitelisted;
}

/**
 * Detects if app is running in embedded mode (iframe).
 *
 * @returns boolean - True if running in iframe, false if standalone
 */
export function isEmbeddedMode(): boolean {
  return window.parent !== window;
}

/**
 * Gets current window origin (for debugging/logging).
 *
 * @returns string - Current window origin (e.g., "https://claims.canassistance.com")
 */
export function getCurrentOrigin(): string {
  return window.location.origin;
}
